/* eslint-disable react-refresh/only-export-components -- Context modules intentionally export provider + hooks. */
import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
  ReactNode,
} from 'react';
import type { User } from '../types/auth';
import { useCurrentUser } from '../services/authHooks';
import { clearLocalStoragePreserving } from '../utils/localStoragePreserver';

export type AuthStatus = 'idle' | 'loading' | 'success' | 'error';

interface AuthState {
  user: User | null;
  status: AuthStatus;
  error: string | null;
  hasAuthenticated: boolean;
}

type AuthAction =
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_STATUS'; payload: AuthStatus }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'LOGOUT' }
  | { type: 'RESET' };

const getInitialHasAuthenticated = () => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem('hasAuthenticated') === 'true';
};

const initialState: AuthState = {
  user: null,
  status: 'idle',
  error: null,
  hasAuthenticated: getInitialHasAuthenticated(),
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_USER':
      if (action.payload) {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('hasAuthenticated', 'true');
          const loginValue =
            action.payload.email || action.payload.username || '';
          if (loginValue) {
            window.localStorage.setItem('lastLogin', loginValue);
          }
        }
      }
      return {
        ...state,
        user: action.payload,
        status: action.payload ? 'success' : 'idle',
        error: null,
        hasAuthenticated: action.payload ? true : state.hasAuthenticated,
      };
    case 'SET_STATUS':
      return { ...state, status: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, status: 'error' };
    case 'LOGOUT':
      // Clear all localStorage data (mappings, upload queues, user-specific settings, etc.)
      clearLocalStoragePreserving({ preserveKeyPrefixes: ['activeRole_'] });
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('hasAuthenticated');
        window.localStorage.removeItem('lastLogin');
      }
      return { ...initialState, hasAuthenticated: false };
    case 'RESET':
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('hasAuthenticated');
        window.localStorage.removeItem('lastLogin');
      }
      return { ...initialState, hasAuthenticated: false };
    default:
      return state;
  }
}

interface AuthContextType extends AuthState {
  setUser: (user: User | null) => void;
  setStatus: (status: AuthStatus) => void;
  setError: (error: string | null) => void;
  logout: () => void;
  reset: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const {
    data: userData,
    isLoading,
    error,
  } = useCurrentUser({
    enabled: true,
  });

  // Sync with useCurrentUser - stabilize dependencies
  useEffect(() => {
    if (isLoading) {
      dispatch({ type: 'SET_STATUS', payload: 'loading' });
    } else if (error) {
      // Only set error if it's not a 401 (unauthorized) - that's expected for non-authenticated users
      if (error.message !== 'Unauthorized') {
        dispatch({ type: 'SET_ERROR', payload: error.message });
      } else {
        dispatch({ type: 'SET_USER', payload: null });
      }
    } else if (userData) {
      // Transform UserFromApi to User
      const user: User = {
        id: userData.id,
        email: userData.email,
        username: userData.username,
        first_name: userData.first_name,
        last_name: userData.last_name,
        roles: userData.roles,
        current_study: userData.current_study,
        permitted_studies: userData.permitted_studies,
      };
      dispatch({ type: 'SET_USER', payload: user });
      dispatch({ type: 'SET_STATUS', payload: 'success' });
    } else {
      dispatch({ type: 'SET_USER', payload: null });
    }
  }, [userData, isLoading, error]);

  // Memoize functions
  const setUser = useCallback((user: User | null) => {
    dispatch({ type: 'SET_USER', payload: user });
  }, []);

  const setStatus = useCallback((status: AuthStatus) => {
    dispatch({ type: 'SET_STATUS', payload: status });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);

  const logout = useCallback(() => {
    dispatch({ type: 'LOGOUT' });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  // Memoize context value
  const value = useMemo<AuthContextType>(
    () => ({
      ...state,
      setUser,
      setStatus,
      setError,
      logout,
      reset,
    }),
    [state, setUser, setStatus, setError, logout, reset]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
